import { Node, mergeAttributes } from '@tiptap/core';
import { getStyleHTMLAttributes, type StyleAttrs } from '../utils/styleHelper';

export interface FaqOptions {
  HTMLAttributes: Record<string, unknown>;
}

/**
 * Creates a style attribute configuration for FAQ extensions.
 * Parses inline styles from HTML and converts them to a style object.
 */
function createStyleAttribute() {
  return {
    default: null,
    parseHTML: (element: HTMLElement) => {
      const styleString = element.getAttribute('style');
      if (!styleString) return null;

      const styleObj: Record<string, string> = {};
      styleString.split(';').forEach((rule) => {
        const [property, value] = rule.split(':').map((s) => s.trim());
        if (property && value) {
          // Convert kebab-case to camelCase
          const camelProperty = property.replace(/-([a-z])/g, (_, letter: string) =>
            letter.toUpperCase()
          );
          styleObj[camelProperty] = value;
        }
      });
      return Object.keys(styleObj).length > 0 ? styleObj : null;
    },
    renderHTML: () => ({}), // Style is rendered separately
  };
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    faqSchema: {
      insertFaq: () => ReturnType;
    };
    faqItem: {
      insertFaqItem: () => ReturnType;
    };
  }
}

// FAQ Schema container (Schema.org FAQPage)
export const FaqSchemaExtension = Node.create<FaqOptions>({
  name: 'faqSchema',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  content: 'faqItem*',

  defining: true,

  isolating: true,

  addAttributes() {
    return {
      style: createStyleAttribute(),
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[itemtype="https://schema.org/FAQPage"]',
      },
      {
        tag: 'div.faq-schema',
      },
      {
        tag: 'section.faq',
      },
      // Template-specific FAQ container class
      {
        tag: 'div.faq-section',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const styleAttrs = getStyleHTMLAttributes(node.attrs as StyleAttrs);

    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, styleAttrs, {
        itemscope: '',
        itemtype: 'https://schema.org/FAQPage',
        // Include both faq-schema and faq-section for template CSS compatibility
        class: 'faq-schema faq-section',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      insertFaq:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            content: [
              {
                type: 'faqItem',
                content: [
                  {
                    type: 'faqQuestion',
                    content: [{ type: 'text', text: 'Question here?' }],
                  },
                  {
                    type: 'faqAnswer',
                    content: [
                      {
                        type: 'paragraph',
                        content: [{ type: 'text', text: 'Answer here.' }],
                      },
                    ],
                  },
                ],
              },
            ],
          });
        },
    };
  },
});

// Individual FAQ item (Schema.org Question)
export const FaqItemExtension = Node.create<FaqOptions>({
  name: 'faqItem',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  content: 'faqQuestion faqAnswer',

  defining: true,

  addAttributes() {
    return {
      style: createStyleAttribute(),
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[itemtype="https://schema.org/Question"]',
      },
      {
        tag: 'div.faq-item',
      },
      {
        tag: 'details.faq-item',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const styleAttrs = getStyleHTMLAttributes(node.attrs as StyleAttrs);

    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, styleAttrs, {
        itemscope: '',
        itemprop: 'mainEntity',
        itemtype: 'https://schema.org/Question',
        class: 'faq-item',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      insertFaqItem:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            content: [
              {
                type: 'faqQuestion',
                content: [{ type: 'text', text: 'New question?' }],
              },
              {
                type: 'faqAnswer',
                content: [
                  {
                    type: 'paragraph',
                    content: [{ type: 'text', text: 'Answer here.' }],
                  },
                ],
              },
            ],
          });
        },
    };
  },
});

// FAQ Question element
export const FaqQuestionExtension = Node.create({
  name: 'faqQuestion',

  group: 'block',

  content: 'inline*',

  defining: true,

  addAttributes() {
    return {
      style: createStyleAttribute(),
    };
  },

  parseHTML() {
    return [
      {
        tag: 'h3[itemprop="name"]',
      },
      {
        tag: 'summary.faq-question',
      },
      {
        tag: 'div.faq-question',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const styleAttrs = getStyleHTMLAttributes(node.attrs as StyleAttrs);

    return [
      'h3',
      mergeAttributes(HTMLAttributes, styleAttrs, {
        itemprop: 'name',
        class: 'faq-question',
      }),
      0,
    ];
  },
});

// FAQ Answer element
export const FaqAnswerExtension = Node.create({
  name: 'faqAnswer',

  group: 'block',

  content: 'block+',

  defining: true,

  addAttributes() {
    return {
      style: createStyleAttribute(),
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[itemtype="https://schema.org/Answer"]',
      },
      {
        tag: 'div.faq-answer',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const styleAttrs = getStyleHTMLAttributes(node.attrs as StyleAttrs);

    return [
      'div',
      mergeAttributes(HTMLAttributes, styleAttrs, {
        itemscope: '',
        itemprop: 'acceptedAnswer',
        itemtype: 'https://schema.org/Answer',
        class: 'faq-answer',
      }),
      ['div', { itemprop: 'text' }, 0],
    ];
  },
});

export default FaqSchemaExtension;
